import {
  type PropsWithChildren,
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
} from 'react';

const sidebarPinStateKey = 'sidebarPinState';
const mobileQuery = '(max-width: 599px)';

type TegelSidebarState = {
  isMobile: boolean;
  isPinned: boolean;
  setPinned: (nextPinned: boolean) => void;
  toggleSidebarPinState: () => void;
};

const TegelSidebarStateContext = createContext<TegelSidebarState>({
  isMobile: false,
  isPinned: true,
  setPinned: () => {},
  toggleSidebarPinState: () => {},
});

const readInitialPinnedState = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const stored = window.localStorage.getItem(sidebarPinStateKey);
    return stored === null ? true : Boolean(JSON.parse(stored));
  } catch {
    return true;
  }
};

export const TegelSidebarStateProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [isPinned, setIsPinned] = useState(readInitialPinnedState);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(mobileQuery);
    const syncMobileState = () => setIsMobile(mediaQuery.matches);

    syncMobileState();
    mediaQuery.addEventListener?.('change', syncMobileState);

    return () => {
      mediaQuery.removeEventListener?.('change', syncMobileState);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(sidebarPinStateKey, JSON.stringify(isPinned));
    }
  }, [isPinned]);

  const value = useMemo(
    () => ({
      isMobile,
      isPinned,
      setPinned: setIsPinned,
      toggleSidebarPinState: () => setIsPinned(current => !current),
    }),
    [isMobile, isPinned],
  );

  return (
    <TegelSidebarStateContext.Provider value={value}>
      {children}
    </TegelSidebarStateContext.Provider>
  );
};

export const useTegelSidebarState = () => useContext(TegelSidebarStateContext);
